import React, { useEffect, useState, useMemo } from "react";
import './PlotOptions.scss';
import { Button } from "@progress/kendo-react-buttons";
import { DropDown } from "components/SideBarItems/DropDown";
import { InputCharactersCounter, NumericInput, TextInput } from "components/SideBarItems/Input";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { store } from "store/store";
import { getPlotOptionsThunk, getQuickPlotsThunk, saveQuickPlotThunk, saveSidebarThunk } from "store/sidebar/thunk";
import { KendoCheckbox } from "components/SideBarItems/Checkbox";
import { sideBarTreeSlice } from "store/sidebar/slice";
import { getPlotThunk } from "store/explore/thunk";
import { IElementType } from "components/SideBarItems/types";
import { selectSelectedTab } from "store/workspace/slice";
import sortAlphabetically from 'helpers/functions/sortAlphabetically';
import { LICENSE_TYPES } from "helpers/constants/app";
import WithRole from "components/userRoles/withRole/WithRole";
import { T_getDatasetsList } from "store/userData/thunk";
import { DropdownTreeFilterSidebar, TooltipTreeFilter } from "components/SideBarItems/DropDownTreeFilter";
import { useTagAttributeFiltering } from "hooks/useTagAttributeFiltering";
import { addCalculatorParametersToDropdown } from 'api/sidebar/processors';
import { T_getParameters } from 'store/propertyCalculator/thunk';
import { selectActiveUser } from 'store/userState/selectors';
import { isReadOnlyAccess } from 'helpers/functions/workspaceHelpers';

export const PlotOptions: React.FC = () => {
    const dispatch = useAppDispatch();
    const [opened, setOpened] = useState<boolean>(true);
    const selectedTab = useAppSelector(selectSelectedTab);
    const userData = useAppSelector(store => store.userData.datasetsList)
    const { workspaceId, plotId: plotIdW, workspaces } = useAppSelector(state => state.workspaceSlice);
    const { plotOptions, quickPlots, category1TagAttributesValues, numericTagAttributesValues } = useAppSelector(state => state.sideBarTreeSlice);
    const { myParameters } = useAppSelector(store => store.propertyCalculatorSlice);
    const user = useAppSelector(selectActiveUser);
    const isReadOnly = isReadOnlyAccess(workspaces.find(ws => ws.id === workspaceId) || null, user?.id);
    const plotId = selectedTab?.id || plotIdW
    
    // Filter tag attributes based on license permissions
    const { filterTagAttributesByAccess } = useTagAttributeFiltering();
    const filteredNumericTagAttributes = filterTagAttributesByAccess(numericTagAttributesValues);
    const filteredCategory1TagAttributes = filterTagAttributesByAccess(category1TagAttributesValues);
    
    // Fetch user calculation parameters for Subsurface Assets
    useEffect(() => {
        if (user?.id) {
            dispatch(T_getParameters({ assetType: 'Subsurface Assets', userId: user.id }));
        }
    }, [user?.id, dispatch]);
    
    // Combine user parameters with numeric tag attributes
    const subsurfaceUserCalculations = useMemo(() => {
        const allUserCalculations = [...myParameters];
        return allUserCalculations.filter(param => 
            param.assetType === 'Subsurface Assets'
        );
    }, [myParameters]);
    
    const xAxisOptions = useMemo(() => {
        const optionsWithCalculations = addCalculatorParametersToDropdown(
            filteredNumericTagAttributes,
            subsurfaceUserCalculations
        );
        return sortAlphabetically(optionsWithCalculations, 'name');
    }, [filteredNumericTagAttributes, subsurfaceUserCalculations]);
    
    const yAxisOptions = useMemo(() => {
        const optionsWithCalculations = addCalculatorParametersToDropdown(
            filteredNumericTagAttributes,
            subsurfaceUserCalculations
        );
        return sortAlphabetically(optionsWithCalculations, 'name');
    }, [filteredNumericTagAttributes, subsurfaceUserCalculations]);
    useEffect(() => {
        dispatch(T_getDatasetsList())
    }, [selectedTab])


    useEffect(() => {
        if (plotId && workspaceId && selectedTab?.type === 1) {
            dispatch(getPlotOptionsThunk({ workspaceId, plotId: selectedTab.id }));
        }
    }, [plotId, workspaceId, selectedTab]);

    const applyPlotAnalysis = async () => {
        const currentState = store.getState();
        const latestPlotOptions = currentState.sideBarTreeSlice.plotOptions;
        await dispatch(saveSidebarThunk({ plotOptions: latestPlotOptions, plotId, workspaceId }));
        await dispatch(getPlotThunk({ plotId, workspaceId }));
    };

    const saveAsQuickPlot = async () => {
        dispatch(sideBarTreeSlice.actions.setPlotOptionsChanged(false));
        await dispatch(saveQuickPlotThunk(plotId));
        await dispatch(getQuickPlotsThunk());
    }

    const handleChangeValue = (obj: { element?: IElementType, value?: unknown, group?: string | null, index?: number }): void => {
        dispatch(sideBarTreeSlice.actions.setPlotOptionItem(obj));
    }

    const handleSaveValues = async (element?: IElementType, group?: string | null) => {
        if (group !== 'serverSideOptions') {
            const currentState = store.getState();
            const latestPlotOptions = currentState.sideBarTreeSlice.plotOptions;
            await dispatch(saveSidebarThunk({ plotOptions: latestPlotOptions, plotId, workspaceId }));
        }
        if (element === 'xTagAttributeId' || element === 'yTagAttributeId')
            await dispatch(getPlotThunk({ plotId, workspaceId }));
    };

    return (
        <div className="plotOptions">
            <div className="contentVisibilityHandler">
                <h3><span onClick={() => setOpened(!opened)} className={opened ? "k-icon k-i-caret-alt-down" : "k-icon k-i-caret-alt-right"}></span>Plot Options</h3>
            </div>
            <div className={opened ? "content" : "content closed"}>
                <InputCharactersCounter
                    label="Plot Title"
                    element="title"
                    max={250}
                    value={plotOptions.title}
                    group={null}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                {/* <DropDown
                    items={plotTypes}
                    label="Plot Type"
                    element="type"
                    group={null}
                    value={plotOptions.type + ''}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                /> */}
                <DropDown
                    items={quickPlots.filter(item=>item.type === selectedTab?.type)}
                    label="QuickPlot"
                    element="quickPlotId"
                    group={null}
                    value={plotOptions.quickPlotId}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <Button themeColor={"primary"} onClick={saveAsQuickPlot}>Save To Quick Plot</Button>
                
                <DropdownTreeFilterSidebar
                    items={xAxisOptions}
                    label="X Axis"
                    element="xTagAttributeId"
                    group={null}
                    value={plotOptions.xTagAttributeId}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <DropdownTreeFilterSidebar
                    items={yAxisOptions}
                    label="Y Axis"
                    element="yTagAttributeId"
                    group={null}
                    value={plotOptions.yTagAttributeId}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <NumericInput
                    label="X Min"
                    element="xMin"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.xMin}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <NumericInput
                    label="X Max"
                    element="xMax"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.xMax}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <NumericInput
                    label="Y Min"
                    element="yMin"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.yMin}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <NumericInput
                    label="Y Max"
                    element="yMax"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.yMax}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="X Gridlines"
                    element="xGridlines"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.xGridlines}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Y Gridlines"
                    element="yGridlines"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.yGridlines}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="X Logarithmic"
                    element="xLogarithmic"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.xLogarithmic}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Y Logarithmic"
                    element="yLogarithmic"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.yLogarithmic}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="X Invert"
                    element="xInvert"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.xInvert}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Y Invert"
                    element="yInvert"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.yInvert}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <h4>Legends</h4>
                <KendoCheckbox
                    label="Show Filters"
                    group="clientSideOptions"
                    element="showFilters"
                    readOnly={isReadOnly}
                    value={plotOptions.crossPlotOptions.showFilters}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Show User Data"
                    group="clientSideOptions"
                    element="showUserData"
                    disabled={!userData.length}
                    readOnly={isReadOnly}
                    value={plotOptions.crossPlotOptions.showUserData}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Show Regression Equations"
                    group="clientSideOptions"
                    element="showRegressionEquations"
                    readOnly={isReadOnly}
                    value={plotOptions.crossPlotOptions.showRegressionEquations}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <h4>Labels</h4>
                <KendoCheckbox
                    disabled={!userData.length}
                    readOnly={isReadOnly}
                    label="Show User Data Labels"
                    element="showUserDataLabels"
                    group="clientSideOptions"
                    value={plotOptions.crossPlotOptions.showUserDataLabels}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <WithRole roles={[LICENSE_TYPES.global]}>
                    <KendoCheckbox
                        label="Show Global Database Labels"
                        element="globalDatabaseLabels"
                        group="clientSideOptions"
                        readOnly={isReadOnly}
                        value={plotOptions.crossPlotOptions.globalDatabaseLabels}
                        setFunction={handleChangeValue}
                        saveFunction={handleSaveValues}
                    />
                </WithRole>
                <KendoCheckbox
                    label="Show Axis Labels"
                    element="showAxisLabels"
                    group="clientSideOptions"
                    readOnly={isReadOnly}
                    value={plotOptions.crossPlotOptions.showAxisLabels}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <div className="hr" />
                <h4>Plot Analysis</h4>
                <h4>Regression</h4>
                <KendoCheckbox
                    label="Lin Regression"
                    group="serverSideOptions"
                    element="linRegression"
                    value={plotOptions.crossPlotOptions.linRegression}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Log Regression"
                    group="serverSideOptions"
                    element="logRegression"
                    value={plotOptions.crossPlotOptions.logRegression}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Exp Regression"
                    group="serverSideOptions"
                    element="expRegression"
                    value={plotOptions.crossPlotOptions.expRegression}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Pow Regression"
                    group="serverSideOptions"
                    element="powRegression"
                    value={plotOptions.crossPlotOptions.powRegression}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <NumericInput
                    label="Y Intcp Value"
                    group="serverSideOptions"
                    element="intcpValue"
                    value={plotOptions.crossPlotOptions.intcpValue}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Y Intcp"
                    group="serverSideOptions"
                    element="intcp"
                    value={plotOptions.crossPlotOptions.intcp}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Add point"
                    group="serverSideOptions"
                    element="addPoint"
                    value={plotOptions.crossPlotOptions.addPoint}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <NumericInput
                    label="X Value"
                    group="serverSideOptions"
                    element="xValue"
                    value={plotOptions.crossPlotOptions.xValue}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <TextInput
                    label="Label"
                    group="serverSideOptions"
                    element="label"
                    value={plotOptions.crossPlotOptions.label}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <h4>Tooltips</h4>
                {new Array(5).fill(null).map((_, i) => (
                    <TooltipTreeFilter
                        key={i}
                        items={sortAlphabetically(filteredCategory1TagAttributes, 'name')}
                        element="tooltips"
                        index={i}
                        group="serverSideOptions"
                        value={plotOptions.crossPlotOptions.tooltips[i]}
                        setFunction={handleChangeValue}
                        saveFunction={handleSaveValues}
                    />
                ))}
                <Button themeColor={"primary"} onClick={applyPlotAnalysis}>Apply Plot Analysis</Button>
                <div className="hr" />
            </div>
        </div>
    )
}

